import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { STORAGE_KEY } from '../../constants';
import { createMessagesSlice } from './messagesSlice';
import { createSelectionSlice } from './selectionSlice';
import { createStreamingSlice } from './streamingSlice';
import { createTabsSlice } from './tabsSlice';
import { createTurnsSlice } from './turnsSlice';
import { createUiSlice } from './uiSlice';
import type { AgentForgeStore } from './types';
import { INITIAL_STATE } from './types';

export const useAgentForgeStore = create<AgentForgeStore>()(
  persist(
    (...a) => ({
      ...INITIAL_STATE,
      ...createTabsSlice(...a),
      ...createUiSlice(...a),
      ...createMessagesSlice(...a),
      ...createStreamingSlice(...a),
      ...createTurnsSlice(...a),
      ...createSelectionSlice(...a),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        openTabs: s.openTabs,
        activeSessionId: s.activeSessionId,
        mode: s.mode,
        historyOpen: s.historyOpen,
        messagesBySession: s.messagesBySession,
        selectionByBlock: s.selectionByBlock,
        activeSelectionBlock: s.activeSelectionBlock,
        draftBySession: s.draftBySession,
        activeTurnsBySession: s.activeTurnsBySession,
      }),
      // A live draft is intentionally ephemeral, but the accepted Temporal
      // turn is durable. Restore streaming only when a persisted active turn
      // backs it; legacy streaming tabs without an identity are downgraded so
      // old localStorage cannot wedge the composer forever.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<AgentForgeStore>;
        const activeTurnsBySession = Object.fromEntries(
          Object.entries(p.activeTurnsBySession ?? {}).map(
            ([sessionId, turn]) => [
              sessionId,
              turn.phase === 'finalizing' || turn.phase === 'cancelling'
                ? turn
                : { ...turn, phase: 'recovering' as const },
            ]
          )
        );
        const openTabs = (p.openTabs ?? []).map((t) => {
          if (activeTurnsBySession[t.sessionId]) {
            return { ...t, status: 'streaming' as const };
          }
          return t.status === 'streaming'
            ? { ...t, status: 'done' as const }
            : t;
        });
        // The minimized presence bar is a per-view state, not a durable
        // preference — persisting it in shared localStorage made it follow the
        // user into every new tab (CORE-4952). Drop it back to the default on
        // hydrate so a fresh tab/reload doesn't auto-show a widget the user
        // never opened there; floating/fullscreen still restore.
        const mode =
          p.mode === 'minimized' ? current.mode : (p.mode ?? current.mode);
        return { ...current, ...p, activeTurnsBySession, openTabs, mode };
      },
    }
  )
);
